/**
 * Cirugias - Sistema de Gestión de Quirófanos Centinela Salud
 * Enhanced UX/UI
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { cirugiasAPI } from '../services/api'
import { useAuthStore, ROLES } from '../store'
import {
  Search, Plus, Calendar, Clock, User, Scissors, X, Eye,
  FileSpreadsheet, Building2, AlertTriangle, CheckCircle, XCircle, Pause, Shield
} from 'lucide-react'
import toast from 'react-hot-toast'

// Datos demo mejorados con hospital_id
const DEMO_CIRUGIAS = [
  { id: 1, paciente_nombre: 'Maria Garcia Lopez', procedimiento: 'Colecistectomia laparoscopica', estado: 'programada', prioridad: 'alta', fecha_programada: '2025-01-05', duracion_estimada: 90, quirofano: 'Q-1', hospital_id: 1, hospital: 'HGZ No. 53 Los Reyes', cirujano: 'Dr. Rodriguez' },
  { id: 2, paciente_nombre: 'Jose Hernandez Martinez', procedimiento: 'Hernioplastia inguinal', estado: 'en_curso', prioridad: 'media', fecha_programada: '2025-01-02', duracion_estimada: 60, quirofano: 'Q-3', hospital_id: 2, hospital: 'HGZ No. 57 La Quebrada', cirujano: 'Dr. Martinez' },
  { id: 3, paciente_nombre: 'Ana Rodriguez Sanchez', procedimiento: 'Apendicectomia', estado: 'completada', prioridad: 'alta', fecha_programada: '2024-12-28', duracion_estimada: 45, quirofano: 'Q-2', hospital_id: 3, hospital: 'HGZ No. 68 Tulpetlac', cirujano: 'Dr. Lopez' },
  { id: 4, paciente_nombre: 'Carlos Lopez Torres', procedimiento: 'Reemplazo de rodilla', estado: 'programada', prioridad: 'media', fecha_programada: '2025-01-10', duracion_estimada: 180, quirofano: 'Q-1', hospital_id: 5, hospital: 'HGR No. 196 Ecatepec', cirujano: 'Dr. Gonzalez' },
  { id: 5, paciente_nombre: 'Laura Martinez Flores', procedimiento: 'Cesarea', estado: 'programada', prioridad: 'alta', fecha_programada: '2025-01-03', duracion_estimada: 60, quirofano: 'Q-4', hospital_id: 1, hospital: 'HGZ No. 53 Los Reyes', cirujano: 'Dra. Sanchez' },
  { id: 6, paciente_nombre: 'Pedro Gonzalez Ramirez', procedimiento: 'Prostatectomia', estado: 'diferida', prioridad: 'baja', fecha_programada: '2025-01-15', duracion_estimada: 120, quirofano: 'Q-2', hospital_id: 4, hospital: 'HGZ No. 76 Xalostoc', cirujano: 'Dr. Torres' },
  { id: 7, paciente_nombre: 'Sofia Sanchez Morales', procedimiento: 'Tiroidectomia', estado: 'programada', prioridad: 'media', fecha_programada: '2025-01-08', duracion_estimada: 90, quirofano: 'Q-1', hospital_id: 6, hospital: 'HGZ No. 197 Texcoco', cirujano: 'Dr. Ramirez' },
  { id: 8, paciente_nombre: 'Miguel Torres Diaz', procedimiento: 'Artroscopia de rodilla', estado: 'cancelada', prioridad: 'baja', fecha_programada: '2024-12-30', duracion_estimada: 45, quirofano: 'Q-3', hospital_id: 2, hospital: 'HGZ No. 57 La Quebrada', cirujano: 'Dr. Martinez' },
  { id: 9, paciente_nombre: 'Rosa Mendez Vega', procedimiento: 'Mastectomia', estado: 'en_curso', prioridad: 'alta', fecha_programada: '2025-01-02', duracion_estimada: 150, quirofano: 'Q-5', hospital_id: 3, hospital: 'HGZ No. 68 Tulpetlac', cirujano: 'Dra. Flores' },
  { id: 10, paciente_nombre: 'Fernando Ruiz Castro', procedimiento: 'Bypass gastrico', estado: 'programada', prioridad: 'media', fecha_programada: '2025-01-12', duracion_estimada: 180, quirofano: 'Q-2', hospital_id: 5, hospital: 'HGR No. 196 Ecatepec', cirujano: 'Dr. Lopez' },
]

// Animated Counter
const AnimatedCounter = ({ value, duration = 800 }) => {
  const [count, setCount] = useState(0)
  useEffect(() => {
    let start = 0
    const end = parseInt(value)
    if (start === end) return
    const timer = setInterval(() => {
      start += Math.ceil(end / (duration / 50))
      if (start >= end) {
        setCount(end)
        clearInterval(timer)
      } else {
        setCount(start)
      }
    }, 50)
    return () => clearInterval(timer)
  }, [value, duration])
  return <span>{count}</span>
}

// KPI Card
const KPICard = ({ title, value, subtitle, icon: IconComponent, color, onClick, active }) => {
  const gradients = {
    blue: 'from-blue-500 via-blue-600 to-indigo-600',
    amber: 'from-amber-500 via-orange-500 to-orange-600',
    emerald: 'from-emerald-500 via-emerald-600 to-teal-600',
    red: 'from-red-500 via-red-600 to-rose-600',
    purple: 'from-purple-500 via-purple-600 to-indigo-600',
  }

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${gradients[color]} shadow-lg text-white cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${active ? 'ring-4 ring-white/50' : ''}`}
    >
      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-4">
          <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
            <IconComponent className="w-5 h-5" />
          </div>
        </div>
        <div className="text-4xl font-bold mb-1 tracking-tight">
          <AnimatedCounter value={value} />
        </div>
        <div className="text-sm font-semibold opacity-95">{title}</div>
        {subtitle && <div className="text-xs opacity-75 mt-1">{subtitle}</div>}
      </div>
    </div>
  )
}

// Surgery Detail Modal
const SurgeryModal = ({ surgery, onClose }) => {
  if (!surgery) return null

  const statusConfig = {
    programada: { color: 'from-blue-500 to-indigo-600', icon: Calendar, label: 'Programada' },
    en_curso: { color: 'from-amber-500 to-orange-500', icon: Clock, label: 'En Curso' },
    completada: { color: 'from-emerald-500 to-teal-600', icon: CheckCircle, label: 'Completada' },
    cancelada: { color: 'from-red-500 to-rose-600', icon: XCircle, label: 'Cancelada' },
    diferida: { color: 'from-purple-500 to-indigo-600', icon: Pause, label: 'Diferida' },
  }

  const config = statusConfig[surgery.estado] || statusConfig.programada

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideUp">
        <div className={`p-6 text-white bg-gradient-to-r ${config.color}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <config.icon className="w-5 h-5" />
                <span className="text-white/90 text-sm font-medium">{config.label}</span>
              </div>
              <h2 className="text-2xl font-bold">{surgery.procedimiento}</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Paciente</p>
              <p className="font-semibold text-gray-800 mt-1">{surgery.paciente_nombre}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Cirujano</p>
              <p className="font-semibold text-gray-800 mt-1">{surgery.cirujano}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Fecha</p>
              <p className="font-semibold text-gray-800 mt-1">{new Date(surgery.fecha_programada).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Duracion</p>
              <p className="font-semibold text-gray-800 mt-1">{surgery.duracion_estimada} min</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Quirofano</p>
              <p className="font-semibold text-gray-800 mt-1">{surgery.quirofano}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Prioridad</p>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                surgery.prioridad === 'alta' ? 'bg-red-100 text-red-700' :
                surgery.prioridad === 'media' ? 'bg-amber-100 text-amber-700' :
                'bg-green-100 text-green-700'
              }`}>{surgery.prioridad}</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Hospital</p>
            <p className="font-semibold text-gray-800 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              {surgery.hospital}
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            {surgery.estado === 'programada' && (
              <>
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  Iniciar Cirugia
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                  <Calendar className="w-4 h-4" />
                  Reprogramar
                </button>
              </>
            )}
            {surgery.estado === 'en_curso' && (
              <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors">
                <CheckCircle className="w-4 h-4" />
                Marcar Completada
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Cirugias() {
  // Auth context
  const user = useAuthStore(state => state.user)
  const isAdmin = user?.rol === ROLES.ADMIN
  const isOperador = user?.rol === ROLES.OPERADOR
  const userHospitalId = user?.hospital_id

  const [cirugias, setCirugias] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterEstado, setFilterEstado] = useState('')
  const [selectedSurgery, setSelectedSurgery] = useState(null)

  useEffect(() => {
    loadCirugias()
  }, [])

  const loadCirugias = async () => {
    try {
      const response = await cirugiasAPI.getAll()
      let data = response.data?.data || response.data || []
      // Filtrar por hospital si es operador
      if ((isOperador || user?.rol === ROLES.HOSPITAL_USER) && userHospitalId) {
        data = data.filter(c => c.hospital_id === userHospitalId)
      }
      setCirugias(data)
    } catch (error) {
      console.error('Error loading cirugias, usando datos demo:', error)
      let demoData = DEMO_CIRUGIAS
      // Filtrar datos demo por hospital si es operador
      if ((isOperador || user?.rol === ROLES.HOSPITAL_USER) && userHospitalId) {
        demoData = demoData.filter(c => c.hospital_id === userHospitalId)
      }
      setCirugias(demoData)
    } finally {
      setLoading(false)
    }
  }

  const filteredCirugias = useMemo(() => {
    return cirugias.filter(c => {
      const matchSearch = !search ||
        c.paciente_nombre?.toLowerCase().includes(search.toLowerCase()) ||
        c.procedimiento?.toLowerCase().includes(search.toLowerCase())
      const matchEstado = !filterEstado || c.estado === filterEstado
      return matchSearch && matchEstado
    })
  }, [cirugias, search, filterEstado])

  const stats = useMemo(() => ({
    programadas: cirugias.filter(c => c.estado === 'programada').length,
    enCurso: cirugias.filter(c => c.estado === 'en_curso').length,
    completadas: cirugias.filter(c => c.estado === 'completada').length,
    canceladas: cirugias.filter(c => c.estado === 'cancelada').length,
    diferidas: cirugias.filter(c => c.estado === 'diferida').length,
  }), [cirugias])

  const handleExport = useCallback(() => {
    const csvContent = [
      ['Paciente', 'Procedimiento', 'Estado', 'Fecha', 'Quirofano', 'Hospital', 'Cirujano'].join(','),
      ...filteredCirugias.map(c =>
        [c.paciente_nombre, c.procedimiento, c.estado, c.fecha_programada, c.quirofano, c.hospital, c.cirujano].join(',')
      )
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cirugias_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('Datos exportados correctamente')
  }, [filteredCirugias])

  const handleResetFilters = () => {
    setSearch('')
    setFilterEstado('')
  }

  const getEstadoConfig = (estado) => {
    const configs = {
      programada: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Calendar },
      en_curso: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
      completada: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      cancelada: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      diferida: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Pause },
    }
    return configs[estado] || configs.programada
  }

  const getPrioridadColor = (prioridad) => {
    const colors = { alta: 'bg-red-500', media: 'bg-amber-500', baja: 'bg-emerald-500' }
    return colors[prioridad] || 'bg-gray-500'
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-emerald-200 rounded-full animate-spin border-t-emerald-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Scissors className="w-6 h-6 text-emerald-600 animate-pulse" />
          </div>
        </div>
        <p className="mt-4 text-gray-500 font-medium">Cargando cirugias...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Cirugias</h1>
            {(isOperador || user?.rol === ROLES.HOSPITAL_USER) && user?.hospital_nombre && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {user.hospital_nombre}
              </span>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            {(isOperador || user?.rol === ROLES.HOSPITAL_USER)
              ? `${cirugias.length} cirugias de tu unidad medica`
              : `${cirugias.length} cirugias registradas`
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-green-50 border border-green-200 mr-2">
              <Shield className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-green-600 font-medium">Modo Administrador</p>
                <p className="text-sm font-semibold text-green-800">Todos los hospitales</p>
              </div>
            </div>
          )}
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all shadow-sm"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Exportar
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-all shadow-sm">
            <Plus className="w-4 h-4" />
            Nueva Cirugia
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Programadas"
          value={stats.programadas}
          subtitle="Pendientes"
          icon={Calendar}
          color="blue"
          onClick={() => setFilterEstado(filterEstado === 'programada' ? '' : 'programada')}
          active={filterEstado === 'programada'}
        />
        <KPICard
          title="En Curso"
          value={stats.enCurso}
          subtitle="Ahora mismo"
          icon={Clock}
          color="amber"
          onClick={() => setFilterEstado(filterEstado === 'en_curso' ? '' : 'en_curso')}
          active={filterEstado === 'en_curso'}
        />
        <KPICard
          title="Completadas"
          value={stats.completadas}
          subtitle="Exitosas"
          icon={CheckCircle}
          color="emerald"
          onClick={() => setFilterEstado(filterEstado === 'completada' ? '' : 'completada')}
          active={filterEstado === 'completada'}
        />
        <KPICard
          title="Diferidas"
          value={stats.diferidas}
          subtitle="Pospuestas"
          icon={Pause}
          color="purple"
          onClick={() => setFilterEstado(filterEstado === 'diferida' ? '' : 'diferida')}
          active={filterEstado === 'diferida'}
        />
        <KPICard
          title="Canceladas"
          value={stats.canceladas}
          subtitle="No realizadas"
          icon={XCircle}
          color="red"
          onClick={() => setFilterEstado(filterEstado === 'cancelada' ? '' : 'cancelada')}
          active={filterEstado === 'cancelada'}
        />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[300px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Paciente o procedimiento..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
              />
            </div>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Estado</label>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all appearance-none cursor-pointer"
            >
              <option value="">Todos</option>
              <option value="programada">Programada</option>
              <option value="en_curso">En Curso</option>
              <option value="completada">Completada</option>
              <option value="diferida">Diferida</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          {(search || filterEstado) && (
            <button
              onClick={handleResetFilters}
              className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Lista de Cirugias */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-xl">
              <Scissors className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Lista de Cirugias</h3>
              <p className="text-xs text-gray-500">{filteredCirugias.length} resultados</p>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredCirugias.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Scissors className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No se encontraron cirugias</p>
              <p className="text-gray-400 text-sm mt-1">Ajusta los filtros para ver resultados</p>
            </div>
          ) : (
            filteredCirugias.map((cirugia) => {
              const estadoConfig = getEstadoConfig(cirugia.estado)
              const EstadoIcon = estadoConfig.icon
              return (
                <div
                  key={cirugia.id}
                  onClick={() => setSelectedSurgery(cirugia)}
                  className="p-5 hover:bg-gray-50/50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-1.5 h-full min-h-[80px] rounded-full ${getPrioridadColor(cirugia.prioridad)}`}></div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-800 text-lg">{cirugia.procedimiento}</h3>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <User size={14} />
                            <span>{cirugia.paciente_nombre}</span>
                          </div>
                        </div>
                        <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${estadoConfig.bg} ${estadoConfig.text}`}>
                          <EstadoIcon size={14} />
                          {cirugia.estado?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-3">
                        <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-lg">
                          <Calendar size={14} className="text-gray-400" />
                          <span>{new Date(cirugia.fecha_programada).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-lg">
                          <Clock size={14} className="text-gray-400" />
                          <span>{cirugia.duracion_estimada} min</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-lg">
                          <Scissors size={14} className="text-gray-400" />
                          <span>{cirugia.quirofano}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-lg">
                          <Building2 size={14} className="text-gray-400" />
                          <span>{cirugia.hospital}</span>
                        </div>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
                      <Eye size={20} />
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedSurgery && (
        <SurgeryModal surgery={selectedSurgery} onClose={() => setSelectedSurgery(null)} />
      )}

      {/* Estilos */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  )
}
